// Klub panel — Helyszín statisztikák (Figma OP_07): bevétel, mérkőzések, munkamenetek (foglalások),
// átlagos időtartam, napi bevétel grafikon, csúcsidők, táblánkénti teljesítmény.
import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { fmtInt, toDate } from '../data';
import { Card, Empty, PageHeader, SectionTitle, Segmented, Spinner, StatCard } from '../ui';
import { bookingPrice, useBookings, useBoards, useClub } from './clubData';

type Range = 'day' | 'week' | 'month' | 'year';

function rangeStart(r: Range): Date {
  const n = new Date();
  if (r === 'day') return new Date(n.getFullYear(), n.getMonth(), n.getDate());
  if (r === 'week') { const d = new Date(n.getFullYear(), n.getMonth(), n.getDate()); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return d; }
  if (r === 'month') return new Date(n.getFullYear(), n.getMonth(), 1);
  return new Date(n.getFullYear(), 0, 1);
}

export default function ClubAnalyticsPage() {
  const { club } = useClub();
  const boards = useBoards(club?.id);
  const bookings = useBookings(club?.id, 400, 0);
  const [range, setRange] = useState<Range>('month');
  const [matches, setMatches] = useState<Date[] | null>(null);

  useEffect(() => {
    if (!club) return;
    getDocs(query(collection(db, 'match_history'), where('venue', '==', club.name), limit(3000)))
      .then((s) => setMatches(s.docs.map((d) => toDate(d.data().date ?? d.data().createdAt)).filter((x): x is Date => Boolean(x))))
      .catch(() => setMatches([]));
  }, [club]);

  const data = useMemo(() => {
    if (!bookings || !boards) return null;
    const from = rangeStart(range);
    const len = Date.now() - from.getTime();
    const prevFrom = new Date(from.getTime() - Math.max(len, 864e5));
    const live = bookings.filter((b) => b.state !== 'cancelled' && b.start <= new Date());
    const cur = live.filter((b) => b.start >= from);
    const prev = live.filter((b) => b.start >= prevFrom && b.start < from);
    const rev = cur.reduce((s, b) => s + bookingPrice(b, boards), 0);
    const revPrev = prev.reduce((s, b) => s + bookingPrice(b, boards), 0);
    const avg = cur.length ? Math.round(cur.reduce((s, b) => s + b.durationMin, 0) / cur.length) : 0;

    // napi bevétel (év nézetben havi)
    const pts: { label: string; v: number }[] = [];
    if (range === 'year') {
      for (let m = 0; m <= new Date().getMonth(); m++) {
        const a = new Date(from.getFullYear(), m, 1); const z = new Date(from.getFullYear(), m + 1, 1);
        pts.push({ label: a.toLocaleString('hu-HU', { month: 'short' }), v: cur.filter((b) => b.start >= a && b.start < z).reduce((s, b) => s + bookingPrice(b, boards), 0) });
      }
    } else {
      const days = Math.max(1, Math.ceil(len / 864e5));
      for (let i = 0; i < days; i++) {
        const a = new Date(from.getTime() + i * 864e5); const z = new Date(a.getTime() + 864e5);
        pts.push({ label: `${a.getMonth() + 1}.${a.getDate()}.`, v: cur.filter((b) => b.start >= a && b.start < z).reduce((s, b) => s + bookingPrice(b, boards), 0) });
      }
    }
    const byHour = Array.from({ length: 24 }, (_, h) => cur.filter((b) => b.start.getHours() === h).length);
    const perBoard = boards.map((bd) => {
      const list = cur.filter((b) => b.board === bd.order);
      return { name: bd.name, n: list.length, rev: list.reduce((s, b) => s + bookingPrice(b, boards), 0) };
    }).sort((a, b) => b.rev - a.rev);
    return { cur, rev, revPrev, avg, pts, byHour, perBoard, from };
  }, [bookings, boards, range]);

  if (!club || !data) return <Spinner />;
  const matchCount = matches?.filter((d) => d >= data.from).length;
  const W = 600, H = 170;
  const max = Math.max(1, ...data.pts.map((p) => p.v));
  const step = data.pts.length > 1 ? W / (data.pts.length - 1) : W;
  const line = data.pts.map((p, i) => `${i ? 'L' : 'M'}${(i * step).toFixed(1)},${(H - 6 - (p.v / max) * (H - 20)).toFixed(1)}`).join(' ');
  const first = data.byHour.findIndex((n) => n > 0);
  const last = 23 - [...data.byHour].reverse().findIndex((n) => n > 0);
  const hours = first < 0 ? [] : Array.from({ length: last - first + 1 }, (_, i) => first + i);
  const maxH = Math.max(1, ...data.byHour);
  const maxRev = Math.max(1, ...data.perBoard.map((p) => p.rev));

  return (
    <div>
      <PageHeader title="Helyszín statisztikák" sub={`${club.name} teljesítménye`}
        right={<Segmented value={range} onChange={setRange} options={[{ value: 'day', label: 'Ma' }, { value: 'week', label: 'Ez a hét' }, { value: 'month', label: 'Ez a hónap' }, { value: 'year', label: 'Ez az év' }]} />} />
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard icon="Coins" tone="green" label="Összes bevétel" value={`${fmtInt(data.rev)} Ft`} valueColor="#2bc48a"
          delta={data.revPrev ? { text: `${Math.abs(Math.round(((data.rev - data.revPrev) / data.revPrev) * 100))}%`, up: data.rev >= data.revPrev } : null} />
        <StatCard icon="Target" tone="cyan" label="Összes mérkőzés" value={matchCount === undefined ? '…' : fmtInt(matchCount)} />
        <StatCard icon="Clock" tone="purple" label="Összes munkamenet" value={fmtInt(data.cur.length)} valueColor="#a855f7" />
        <StatCard icon="Clock" tone="gold" label="Átl. időtartam" value={`${data.avg} perc`} valueColor="#d9a514" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1.6fr_1fr] gap-4 mt-6">
        <Card className="!p-5">
          <SectionTitle>{range === 'year' ? 'Bevétel havonta' : 'Bevétel naponta'}</SectionTitle>
          {data.rev === 0 ? <Empty>Nincs bevétel ebben az időszakban.</Empty> : (
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[170px]" preserveAspectRatio="none">
              <defs><linearGradient id="dqRev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2bc48a" stopOpacity="0.35" /><stop offset="100%" stopColor="#2bc48a" stopOpacity="0" /></linearGradient></defs>
              {[0, 1, 2, 3].map((i) => <line key={i} x1="0" x2={W} y1={(H / 3) * i} y2={(H / 3) * i} stroke="#31383D" strokeOpacity="0.6" vectorEffect="non-scaling-stroke" />)}
              <path d={`${line} L${W},${H} L0,${H} Z`} fill="url(#dqRev)" />
              <path d={line} fill="none" stroke="#2bc48a" strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
            </svg>
          )}
        </Card>
        <Card className="!p-5">
          <SectionTitle>Csúcsidők</SectionTitle>
          {hours.length === 0 ? <Empty>Nincs adat.</Empty> : (
            <div className="flex items-end gap-2 h-[170px]">
              {hours.map((h) => (
                <div key={h} className="flex-1 flex flex-col items-center justify-end h-full gap-1.5" title={`${data.byHour[h]} foglalás`}>
                  <div className="w-full rounded-t-md bg-gradient-to-b from-[#00AFF2] to-[#0b6f9a]" style={{ height: `${Math.max(4, (data.byHour[h] / maxH) * 140)}px` }} />
                  <span className="text-[10px] text-[#8a8f99]">{h}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="mt-6 bg-[#15191E] border border-[#31383D] rounded-2xl overflow-hidden">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-[#1d232a] text-[12px] uppercase text-[#8a8f99]">
              <th className="pl-5 pr-4 h-[39px] font-semibold w-[40%]">Gép / Tábla</th>
              <th className="px-4 font-semibold w-[15%]">Munkamenetek</th>
              <th className="px-4 font-semibold w-[15%]">Bevétel</th>
              <th className="px-4 font-semibold w-[30%]">Teljesítmény</th>
            </tr>
          </thead>
          <tbody>
            {data.perBoard.map((p, i) => (
              <tr key={p.name} className="border-t border-[#31383D] h-[46px]">
                <td className="pl-5 pr-4 text-[13px] font-semibold text-white">{p.name}</td>
                <td className="px-4 text-[13px] text-[#B8B8C3]">{p.n}</td>
                <td className="px-4 text-[13px] text-[#B8B8C3]">{fmtInt(p.rev)} Ft</td>
                <td className="px-4 pr-6">
                  <span className="block h-1.5 rounded-full bg-[#31383D] overflow-hidden">
                    <span className="block h-full rounded-full" style={{ width: `${(p.rev / maxRev) * 100}%`, background: i < 2 ? '#2bc48a' : '#00AFF2' }} />
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.perBoard.length === 0 && <Empty>Még nincs tábla.</Empty>}
      </div>
    </div>
  );
}
